import { useSyncExternalStore } from "react";

/**
 * Restarts this browser requested. ctrl restarts an emulator with a
 * Kubernetes rolling restart: the old pod keeps reporting RUNNING until
 * the new one replaces it, and the API has no restarting state, so the UI
 * tracks the request itself until it sees the restart land.
 */
type Restart = { at: number; sawDown: boolean };

/** Give up after this long; the device should be back well before. */
export const RESTART_TIMEOUT_MS = 4 * 60_000;
const KEY = "na:restarts";

const load = (): Record<string, Restart> => {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) ?? "{}");
  } catch {
    return {};
  }
};

let state: Record<string, Restart> = load();
const listeners = new Set<() => void>();
const save = (next: Record<string, Restart>) => {
  state = next;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable */
  }
  listeners.forEach((l) => l());
};

export function markRestart(id: string) {
  save({ ...state, [id]: { at: Date.now(), sawDown: false } });
}

export function clearRestart(id: string) {
  if (!state[id]) return;
  const { [id]: _, ...rest } = state;
  save(rest);
}

/**
 * Feed the live status in; returns whether a restart is still in flight.
 * Ends when the live status dips below Running and comes back, or on
 * timeout. (The emulator view also ends it when the stream reconnects.)
 */
export function observeRestart(id: string, liveRunning: boolean): boolean {
  const r = state[id];
  if (!r) return false;
  if (Date.now() - r.at > RESTART_TIMEOUT_MS) {
    queueMicrotask(() => clearRestart(id));
    return false;
  }
  if (!liveRunning && !r.sawDown) {
    queueMicrotask(() => save({ ...state, [id]: { ...r, sawDown: true } }));
  } else if (liveRunning && r.sawDown) {
    queueMicrotask(() => clearRestart(id));
    return false;
  }
  return true;
}

export function restartStartedAt(id: string): number | undefined {
  return state[id]?.at;
}

export function useRestarts() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
  );
}
